import { apiFetch } from "../utils/apiFetch";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import WorkspaceListShimmer from "./WorkspaceListShimmer";
import { FaArrowRight, FaSpinner } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import AppNavigation from "./AppNavigation";
import { FiImage, FiX, FiUsers } from "react-icons/fi";
import { useAccountQuery } from "../utils/queries";
import { useQueryClient } from "@tanstack/react-query";
import GroupPricingFields from "./GroupPricingFields";

const WorkSpacesList = () => {
  const [workSpaceName, setWorkSpaceName] = useState("");
  const navigate = useNavigate();
  const { userId, userInfo } = useAuth();
  const client = useQueryClient();
  const groupsQuery = useAccountQuery(["groups"], "/workspaces");
  const workspaces = groupsQuery.data?.workspaces;
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("academics");
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState("");
  const [uploadedThumbnail, setUploadedThumbnail] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creationError, setCreationError] = useState("");
  const [pricing, setPricing] = useState({ access: "free", currency: "INR", amount: "" });
  useEffect(() => {
    if (!thumbnailFile) { setThumbnailPreview(""); return; }
    const url = URL.createObjectURL(thumbnailFile);
    setThumbnailPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnailFile]);
  const chooseThumbnail = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setCreationError("Choose a JPG, PNG, or WebP image up to 5 MB.");
      return;
    }
    setCreationError(""); setUploadedThumbnail(null); setThumbnailFile(file);
  };
  const [newWorkspacePopup, setNewWorkspacePopup] = useState(false);
  const [courseCreationSpinner, setCourseCreationSpinner] = useState(false);
  // const [canCreateCourse, setCanCreateCourse] = useState(false);
  const canCreateCourse = !userInfo?.subscriptionInfo?.status
    ? null
    : ["active", "authenticated", "pending"].includes(
          userInfo?.subscriptionInfo?.status,
        )
      ? true
      : false;

  useEffect(() => {
    if (!courseCreationSpinner || canCreateCourse === null) return;

    setCourseCreationSpinner(false);
    if (canCreateCourse) {
      setNewWorkspacePopup(true);
    } else {
      navigate("/choose-plan");
    }
  }, [courseCreationSpinner, canCreateCourse, navigate]);


  const createWorkSpace = async () => {
    if (creating) return;
    if (canCreateCourse === false) {
      navigate(`/choose-plan`);
      return;
    }
    if (canCreateCourse === null) {
      setCourseCreationSpinner(true);
      return;
    }

    if (workSpaceName.trim() === "") {
      setCreationError("Group name is required.");
      return;
    }
    if (pricing.access === "paid" && (!/^\d{1,7}(\.\d{1,2})?$/.test(pricing.amount) || Number(pricing.amount) < 1)) {
      setCreationError("Enter a price of at least INR 1.00 with at most two decimal places.");
      return;
    }

    setCreating(true);
    setCreationError("");
    try {
      let upload = uploadedThumbnail;
      if (thumbnailFile && !upload) {
        const response = await apiFetch(`${process.env.API_URL}/api/groups/thumbnail`, {
          method: "POST", headers: { "Content-Type": thumbnailFile.type }, body: thumbnailFile,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not upload thumbnail");
        upload = data;
        setUploadedThumbnail(data);
      }
      const res = await apiFetch(`${process.env.API_URL}/api/workspaceCreate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",

        },
        body: JSON.stringify({
          workSpaceName,
          description,
          category,
          thumbnailToken: upload?.thumbnailToken,
          pricing,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        if (res.status === 400) setUploadedThumbnail(null);
        throw new Error(data.message || "Could not create group");
      }

      const data = await res.json();
      client.invalidateQueries({ queryKey: ["account", userId, "groups"] });
      navigate(`/courses/${data.slug}`);
    } catch (err) {
      setCreationError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const roleFinder = (workspace) => {
    const role = workspace.members
      .find((member) => String(member.userId) === String(userId))
      .role.toLowerCase();
    if (role === "admin") {
      return (
        <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-secondary/90 bg-blue-100 text-blue-800 border-blue-200">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-shield h-3 w-3 mr-1"
            data-fg-1us4="2.9:2.11397:/src/app/components/WorkspacesPage.tsx:41:13:1569:35:e:Shield::::::B8zf"
            data-fgid-1us4=":r1e3:"
          >
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
          </svg>
          Admin
        </span>
      );
    } else if (role === "owner") {
      return (
        <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-secondary/90 bg-purple-100 text-purple-800 border-purple-200">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="lucide lucide-crown h-3 w-3 mr-1"
            data-fg-1us1="2.9:2.11397:/src/app/components/WorkspacesPage.tsx:34:13:1345:34:e:Crown::::::okn"
            data-fgid-1us1=":r1fk:"
          >
            <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path>
            <path d="M5 21h14"></path>
          </svg>
          Owner
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center justify-center rounded-md border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:bg-secondary/90 bg-gray-100 text-gray-800 border-gray-200">
          Member
        </span>
      );
    }
  };


  const categoryOptions = [
    { value: "academics", label: "Academics" },
    { value: "music", label: "Music" },
    { value: "fitness", label: "Fitness" },
    { value: "technology", label: "Technology" },
    { value: "business", label: "Business" },
    { value: "arts", label: "Arts" },
    { value: "language", label: "Language" },
    { value: "lifestyle", label: "Lifestyle" },
    { value: "other", label: "Other" },
  ];

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <AppNavigation />
        <div className="max-w-7xl mx-auto px-6 py-8">
          {groupsQuery.error && !workspaces ? <div role="alert" className="p-4 text-sm text-red-700">{groupsQuery.error.message}<button onClick={() => groupsQuery.refetch()} className="ml-3 underline">Try again</button></div> : !workspaces ? (
            <WorkspaceListShimmer />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workspaces.map((workspace) => {
                return (
                  <div key={workspace._id}>
                    <Link to={`/courses/${workspace.slug}`}>
                      <div className="bg-white text-card-foreground flex flex-col gap-6 overflow-hidden rounded-lg border h-full hover:shadow-lg transition-shadow cursor-pointer group border-gray-200">
                        {workspace.thumbnail ? <img src={workspace.thumbnail} alt={`${workspace.name} thumbnail`} className="aspect-video w-full bg-gray-50 object-cover" /> : <div className="flex aspect-video items-center justify-center bg-gray-100 text-gray-400"><FiUsers aria-hidden="true" className="h-10 w-10" /></div>}
                        <div className="@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex-1">
                                <h4 className="leading-none font-bold text-md group-hover:text-blue-600 transition-colors">
                                  {workspace.name}
                                </h4>
                                <p className="text-muted-foreground mt-1">
                                  {workspace.description}
                                  {/* Lorem ipsum dolor sit amet, consectetur
                                  adipiscing elit. */}
                                </p>
                              </div>
                            </div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="lucide lucide-arrow-right h-5 w-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all"
                              data-fg-1us43="2.9:2.11397:/src/app/components/WorkspacesPage.tsx:98:21:3972:115:e:ArrowRight::::::s5N"
                              data-fgid-1us43=":r73:"
                            >
                              <path d="M5 12h14"></path>
                              <path d="m12 5 7 7-7 7"></path>
                            </svg>
                          </div>
                        </div>
                        <div className="px-6 [&:last-child]:pb-6">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1 text-gray-600">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="lucide lucide-users h-4 w-4"
                                  data-fg-1us48="2.9:2.11397:/src/app/components/WorkspacesPage.tsx:105:25:4401:29:e:Users::::::DV8M"
                                  data-fgid-1us48=":r16:"
                                >
                                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                  <circle cx="9" cy="7" r="4"></circle>
                                  <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                  <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                                <span>{workspace.members.length} members</span>
                              </div>

                              {roleFinder(workspace)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
              <div
                onClick={() => {
                  if (courseCreationSpinner) return;
                  if (canCreateCourse === null) {
                    setCourseCreationSpinner(true);
                    return;
                  }
                  if (!canCreateCourse) {
                    navigate("/choose-plan");
                    return;
                  }
                  setNewWorkspacePopup(true);
                }}
                className="text-card-foreground flex flex-col gap-6 rounded-xl h-full hover:shadow-lg transition-shadow cursor-pointer group border-2 border-dashed border-gray-300 hover:border-blue-500 bg-gray-50 hover:bg-blue-50"
              >
                <div className="[&:last-child]:pb-6 flex flex-col items-center justify-center h-full p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center mb-4 transition-colors">
                    {!courseCreationSpinner ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="lucide lucide-plus h-8 w-8 text-blue-600"
                        data-fg-1us62="2.9:2.11397:/src/app/components/WorkspacesPage.tsx:125:21:5523:42:e:Plus::::::ISW"
                        data-fgid-1us62=":r1hi:"
                      >
                        <path d="M5 12h14"></path>
                        <path d="M12 5v14"></path>
                      </svg>
                    ) : (
                      <span role="status" aria-label="Checking subscription">
                        <FaSpinner
                          aria-hidden="true"
                          className="h-8 w-8 animate-spin text-blue-600"
                        />
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Create Group
                  </h3>
                  <p className="text-sm text-gray-600">
                    Start a new group for your team
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="mt-12 bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-2">
              What are groups?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Groups are shared environments where teams can collaborate,
              communicate, and organize their work. Each group has its own
              channels, members, and settings.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-users h-5 w-5 text-blue-600"
                    data-fg-1us120="2.9:2.11397:/src/app/components/WorkspacesPage.tsx:223:17:10062:43:e:Users::::::DV8M"
                    data-fgid-1us120=":r1hu:"
                  >
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">
                    Invite members
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Add teammates and collaborate together
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-settings h-5 w-5 text-purple-600"
                    data-fg-1us128="2.9:2.11397:/src/app/components/WorkspacesPage.tsx:232:17:10547:48:e:Settings::::::D4nh"
                    data-fgid-1us128=":r1i4:"
                  >
                    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">
                    Customize Settings
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Configure group to fit your needs
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="lucide lucide-crown h-5 w-5 text-orange-600"
                    data-fg-1us136="2.9:2.11397:/src/app/components/WorkspacesPage.tsx:241:17:11039:44:e:Crown::::::okn"
                    data-fgid-1us136=":r1ia:"
                  >
                    <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path>
                    <path d="M5 21h14"></path>
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">
                    Manage Permissions
                  </h4>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Control who can access what
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {newWorkspacePopup && (
        <div
          onClick={() => { if (!creating) setNewWorkspacePopup(false); }}
          className="fixed inset-0 bg-black/80 flex justify-center items-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog" aria-modal="true" aria-label="Create a New Group"
            className="bg-white w-[90dvw] max-w-lg max-h-[90dvh] overflow-y-auto p-6 gap-4 grid rounded-lg"
          >
            <div className="flex flex-col gap-2 text-center sm:text-left">
              <h2 className="text-lg leading-none font-semibold">
                Create a New Group
              </h2>
              <p className="text-muted-foreground text-[#717182] text-sm">
                Set up a new group for your team to collaborate and
                communicate.
              </p>
            </div>
            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <label htmlFor="group-thumbnail" className="block text-sm font-medium">Thumbnail <span className="font-normal text-gray-500">(optional)</span></label>
                {thumbnailPreview ? <div className="relative">
                  <img src={thumbnailPreview} alt="Group thumbnail preview" className="aspect-video w-full rounded-md bg-gray-50 object-contain" />
                  <button type="button" disabled={creating} title="Remove thumbnail" aria-label="Remove thumbnail" onClick={() => { setThumbnailFile(null); setUploadedThumbnail(null); }} className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700"><FiX /></button>
                </div> : <div className="flex h-24 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50"><FiImage aria-hidden="true" className="h-7 w-7 text-gray-400" /></div>}
                <input id="group-thumbnail" type="file" accept="image/jpeg,image/png,image/webp" disabled={creating} onChange={chooseThumbnail} className="block w-full min-w-0 text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-orange-50 file:px-3 file:py-2 file:text-orange-800" />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="workspace-name"
                  className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                >
                  Group Name
                </label>
                <input
                  id="workspace-name"
                  disabled={creating}
                  type="text"
                  placeholder="e.g., Marketing Group"
                  className="file:text-foreground bg-[#f3f3f5] placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-none flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-gray-500/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive "
                  value={workSpaceName}
                  onChange={(e) => setWorkSpaceName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="workspace-description"
                  className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                >
                  Description
                </label>
                <textarea
                  id="workspace-description"
                  disabled={creating}
                  placeholder="What's this group for?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="resize-none bg-[#f3f3f5] border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-gray-500/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16 w-full rounded-md border bg-input-background px-3 py-2 text-base transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm border-none"
                ></textarea>
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="workspace-category"
                  className="flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50"
                >
                  Category
                </label>
                <select
                  id="workspace-category"
                  disabled={creating}
                  className="file:text-foreground bg-[#f3f3f5] placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-none flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base bg-input-background transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-gray-500/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive "
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <GroupPricingFields value={pricing} onChange={setPricing} disabled={creating} />
            {creationError && <p role="alert" className="text-sm text-red-700">{creationError}</p>}
            <div className="flex justify-end gap-3">
              <button disabled={creating} onClick={() => setNewWorkspacePopup(false)} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive border bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 h-9 px-4 py-2 has-[>svg]:px-3 border-gray-300 hover:bg-gray-200">
                Cancel
              </button>
              <button
                onClick={createWorkSpace}
                disabled={creating} aria-busy={creating}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-black text-primary-foreground hover:bg-black/90 h-9 px-4 py-2 has-[>svg]:px-3 text-white"
              >
                {creating ? <FaSpinner aria-label="Creating group" className="animate-spin" /> : "Create Group"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkSpacesList;
